import { useState, useEffect } from 'react';
import PrivateApi from '../../services/PrivateApiService';
import { useQuery } from 'react-query';
import SplashPage from '../SplashPage';
import { Col, Container, Row } from 'react-bootstrap';
import EnergoTooltip from '../../components/shared/EnergoTooltip';
import InvoiceClassCard from '../../components/shared/InvoiceClassCard';
import PrivateDocDownloadList from '../../components/customer/private_download/PrivateDocDownloadList';
import ErrorPage from '../ErrorPage';

/**
 * Download Document Page
 * This component fetches and displays downloadable documents for the user,
 * including invoices, other related documents and history documents.
 * 
 * @returns {JSX.Element} - Page content.
 */
const DownloadDocumentPage = () => {

    const API = new PrivateApi(); // Initialize the API service
    const [selectedInvoiceType, setSelectedInvoiceType] = useState([]) // State to hold selected invoice types
    const [hideQuickAccessCards] = useState(false); // State to control visibility of quick access cards
    const [types] = useState([ // Types of invoices and documents
        { id: "poolrechnungCard", name: "Poolrechnungen", count: 0, last: "" },
        { id: "abrechnungenCard", name: "Energielieferant", count: 0, last: "" },
        { id: "vorschreibungCard", name: "Vorschreibungen", count: 0, last: "" },
    ]);
    const [notDownloadedInvoices, setNotDownloadedInvoices] = useState([]) // State to hold invoices that have not been downloaded
    const [oldInvoices, setOldInvoice] = useState([]); // State to hold already downloaded invoices

    // Fetch invoices from the backend
    const { data, error, isLoading } = useQuery("GET_ALL_INVOICES_PRIVATE", API.get_InvoicesPrivateUser, { staleTime: Infinity });

    // Effect to process the fetched data and set initial states
    // It categorizes the invoices into downloaded and not downloaded, and sets the types with counts and last dates.
    // It also updates the selected invoice types based on the fetched data.
    // The data is sorted by creation date for both downloaded and not downloaded invoices.
    // The component will re-render when the data changes.
    // The useEffect hook is used to handle side effects in functional components.
    // It runs when the component mounts and whenever the data changes.
    // The data is expected to have a structure with 'poolrechnungen', 'abrechnungen', and 'vorschreibungen' arrays.
    useEffect(() => {
        if (data) {
            let alreadyDownloadedList = []; // temp array to hold already downloaded invoices
            let noDownloadedList = []; // temp array to hold invoices that have not been downloaded

            // * if Poolrechnungen exist in data process them
            if (data.data.poolrechnungen.length > 0) {
                // Add poolrechnungCard to selectedInvoiceType
                setSelectedInvoiceType((prev) => [...prev, "poolrechnungCard"]);

                // Iterate through each poolrechnung and categorize them based on download status
                data.data.poolrechnungen.forEach(pool => {
                    if (pool.x_datum_download === "False") noDownloadedList.push(pool); // If not downloaded, add to noDownloadedList
                    else alreadyDownloadedList.push(pool); // If downloaded, add to alreadyDownloadedList
                });

                types[0].count = Number(data.data.poolrechnungen.length); // Set the count of poolrechnungen
                types[0].last = new Date(data.data.poolrechnungen[0].create_date).toLocaleDateString('DE-de'); // Set the last date of poolrechnungen
            }
            // * if Abrechnungen exist in data process them
            if (data.data.abrechnungen.length > 0) {
                // Add abrechnungenCard to selectedInvoiceType
                setSelectedInvoiceType((prev) => [...prev, "abrechnungenCard"]);

                // Iterate through each abrechnung and categorize them based on download status
                data.data.abrechnungen.forEach(abr => {
                    if (
                        (abr.x_file_abrechnung_nam !== "False" && abr.x_datum_download === "False") || 
                        (abr.x_file_mahnung_name !== "False" && abr.x_datum_download_mahnung === "False") ||
                        (abr.x_file_stornorechnung_name !== "False" && abr.x_datum_download_storno === "False")
                    ) noDownloadedList.push(abr); // If not downloaded, add to noDownloadedList
                    else alreadyDownloadedList.push(abr); // If downloaded, add to alreadyDownloadedList
                });

                types[1].count = Number(data.data.abrechnungen.length); // Set the count of abrechnungen
                types[1].last = new Date(data.data.abrechnungen[0].create_date).toLocaleDateString('DE-de'); // Set the last date of abrechnungen

            }

            // * if Vorschreibungen exist in data process them
            if (data.data.vorschreibungen.length > 0) {
                
                // Add vorschreibungCard to selectedInvoiceType
                setSelectedInvoiceType((prev) => [...prev, "vorschreibungCard"]);

                // Iterate through each vorschreibung and categorize them based on download status
                data.data.vorschreibungen.forEach(vor => {
                    if (
                        (vor.x_file_vorschreibung_name !== "False" && vor.x_datum_download === "False") ||
                        (vor.x_file_mahnung_name !== "False" && vor.x_datum_download_mahnung === "False")
                    ) noDownloadedList.push(vor); // If not downloaded, add to noDownloadedList
                    else alreadyDownloadedList.push(vor); // If downloaded, add to alreadyDownloadedList
                });

                types[2].count = Number(data.data.vorschreibungen.length); // Set the count of vorschreibungen
                types[2].last = new Date(data.data.vorschreibungen[0].create_date).toLocaleDateString('DE-de'); // Set the last date of vorschreibungen
            }

            // if lists are not empty sort them by create_date
            if (noDownloadedList.length > 0) {
                noDownloadedList.sort((a, b) => new Date(b.create_date) - new Date(a.create_date));
            }
            if (alreadyDownloadedList.length > 0) {
                alreadyDownloadedList.sort((a, b) => new Date(b.create_date) - new Date(a.create_date));
            }

            // Set the state with the sorted lists
            setOldInvoice(alreadyDownloadedList);
            setNotDownloadedInvoices(noDownloadedList);
        }
    }, [data]);

    // Effect to handle the selection of invoice types
    // It updates the oldInvoices and notDownloadedInvoices based on the selected invoice types.
    // The useEffect hook is used to handle side effects in functional components.
    // It runs whenever the selectedInvoiceType changes.
    // It categorizes the invoices into downloaded and not downloaded based on the selected types.
    // The data is expected to have a structure with 'poolrechnungen', 'abrechnungen', and 'vorschreibungen' arrays.
    // The component will re-render when the selectedInvoiceType changes.
    // It sorts the lists by creation date for both downloaded and not downloaded invoices.
    // The sorting is done in descending order, so the most recent invoices appear first.
    // This effect is used to filter and sort the invoices based on the user's selection.
    // It ensures that the component displays the correct invoices based on the user's selection.
    // The selectedInvoiceType is an array that holds the types of invoices the user has selected.
    // The oldInvoices state holds the invoices that have already been downloaded.
    // The notDownloadedInvoices state holds the invoices that have not been downloaded yet.
    // The component will re-render when the selectedInvoiceType changes.
    // The useEffect hook is used to handle side effects in functional components.
    // It runs whenever the selectedInvoiceType changes.
    useEffect(() => {
        let alreadyDownloadedList = []; // temp array to hold already downloaded invoices
        let noDownloadedList = []; // temp array to hold invoices that have not been downloaded

        // Check which invoice types are selected and process them accordingly
        if (selectedInvoiceType.includes("poolrechnungCard")) {
            data.data.poolrechnungen.forEach(pool => {
                if (pool.x_datum_download === "False") noDownloadedList.push(pool);
                else alreadyDownloadedList.push(pool);
            });
        }
        if (selectedInvoiceType.includes("abrechnungenCard")) {
            data.data.abrechnungen.forEach(abr => {
                if (
                    (abr.x_file_abrechnung_nam !== "False" && abr.x_datum_download === "False") || 
                    (abr.x_file_mahnung_name !== "False" && abr.x_datum_download_mahnung === "False") ||
                    (abr.x_file_stornorechnung_name !== "False" && abr.x_datum_download_storno === "False")
                ) noDownloadedList.push(abr);
                else alreadyDownloadedList.push(abr); 
            });
        }
        if (selectedInvoiceType.includes("vorschreibungCard")) {
            data.data.vorschreibungen.forEach(vor => {
                if (
                    (vor.x_file_vorschreibung_name !== "False" && vor.x_datum_download === "False") ||
                    (vor.x_file_mahnung_name !== "False" && vor.x_datum_download_mahnung === "False")
                ) noDownloadedList.push(vor);
                else alreadyDownloadedList.push(vor);
            });
        }
        if (noDownloadedList.length > 0) {
            noDownloadedList.sort((a, b) => new Date(b.create_date) - new Date(a.create_date));
        }
        if (alreadyDownloadedList.length > 0) {
            alreadyDownloadedList.sort((a, b) => new Date(b.create_date) - new Date(a.create_date));
        }
        setOldInvoice(alreadyDownloadedList);
        setNotDownloadedInvoices(noDownloadedList);
    }, [selectedInvoiceType]);

    // Handle the loading state
    if (isLoading) return <SplashPage />;

    // Handle the error state Da ist etwas schief gelaufen. Bitte versuchen Sie es später nochmal.
    if (error) return <ErrorPage error={error} />;

    // Render the component
    return (
        <>
            <Container fluid className="text-start">
                <Row>
                    <Col className='flex'>
                        <h3 style={{ marginRight: 10 }}>Dokumente</h3>
                        <EnergoTooltip tKey="5002" position="right" />
                    </Col>
                </Row>
                <Row>
                    <small>
                        Hier finden Sie all Ihre neuen Dokumente zu laufenden Energielieferverträgen zum Download.
                        Wenn die Dokumente bereits heruntergeladen wurden, navigieren Sie auf <a href="/dashboard/anlagen/" className="link-energo">"Meine Anlagen"</a> und
                        wählen Sie "Energieliefervertrag ansehen". Dort finden Sie die Unterlagen am Seitenende.
                    </small>
                </Row>
                <Row hidden={hideQuickAccessCards} className="mt-5">
                    <h5 className="text-energo">Schnellzugriff</h5>
                    {types.map(t => (
                        <InvoiceClassCard
                            select={setSelectedInvoiceType}
                            selected={selectedInvoiceType}
                            type={t}
                            key={t.id}
                        />
                    ))}
                </Row>

                <Row className='mt-3'>
                    <Col>
                        <PrivateDocDownloadList
                            notDownloadedInvoices={notDownloadedInvoices}
                            oldInvoices={oldInvoices}
                        />
                    </Col>
                </Row>
            </Container>
        </>
    );
}

export default DownloadDocumentPage;